import React from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { formatBreakDuration, leavePalette, statusMeta } from "./attendanceHelpers";

// Presentational history table for one attendance type. It renders whatever
// already-filtered/paginated rows it's handed against the active saved-view
// columns (`headers`), and every row defers its detail to the existing
// EditWorkAdjustment drawer via onRowClick, so no approval/edit logic lives here.
const headCellSx = {
  color: "#000000",
  fontWeight: 700,
  fontSize: 12,
  borderBottom: `1px solid ${leavePalette.border}`,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  borderBottom: `1px solid ${leavePalette.border}`,
  fontSize: 13,
  color: leavePalette.text,
};

const dash = (v) => (v === 0 || v ? v : "—");

const renderAttendanceCell = (row, head, meta) => {
  switch (head.key) {
    case "type_label": {
      const Icon = meta?.icon;
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${meta?.color}1A`,
              color: meta?.color,
            }}
          >
            {Icon ? <Icon sx={{ fontSize: 16 }} /> : null}
          </Box>
          <Typography fontWeight={600} sx={{ fontSize: 13 }}>
            {row.type_label || meta?.label}
          </Typography>
        </Stack>
      );
    }
    case "status": {
      const color = statusMeta(row.status, row.color_code);
      return (
        <Box
          sx={{
            display: "inline-block",
            px: 1.2,
            py: 0.3,
            borderRadius: 5,
            fontSize: 11,
            fontWeight: 700,
            color,
            bgcolor: `${color}1A`,
          }}
        >
          {row.status || "—"}
        </Box>
      );
    }
    case "reason":
      return (
        <Box
          sx={{
            maxWidth: 180,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.reason || "—"}
        </Box>
      );
    case "applied":
      return (
        <Typography
          component="span"
          sx={{ fontSize: 13, color: leavePalette.subText, whiteSpace: "nowrap" }}
        >
          {dash(row.applied)}
        </Typography>
      );
    case "duration":
      return meta?.key === "break" ? formatBreakDuration(row.duration) : dash(row.duration);
    default:
      return dash(row[head.key]);
  }
};

const AttendanceHistoryMobileCard = ({ row, headers, meta, onClick }) => {
  const Icon = meta?.icon;
  const visibleFields = headers.filter((head) => head.key !== "type_label");
  const primaryField = visibleFields[0];
  const statusField = visibleFields.find((head) => head.key === "status");

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5,
        border: `1px solid ${leavePalette.border}`,
        borderRadius: 2,
        backgroundColor: "#fff",
        cursor: "pointer",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${meta?.color}1A`,
            color: meta?.color,
            flexShrink: 0,
          }}
        >
          {Icon ? <Icon sx={{ fontSize: 18 }} /> : null}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: leavePalette.text,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.type_label || meta?.label}
            </Typography>
            {statusField ? renderAttendanceCell(row, statusField, meta) : null}
          </Stack>
          {primaryField ? (
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 13,
                color: leavePalette.subText,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {primaryField.name}: {dash(row[primaryField.key])}
            </Typography>
          ) : null}
          <Stack spacing={0.75} sx={{ mt: 1.25 }}>
            {visibleFields
              .filter(
                (head) =>
                  head.key !== primaryField?.key && head.key !== "status",
              )
              .map((head) => (
                <Stack
                  key={head.key}
                  direction="row"
                  spacing={1}
                  justifyContent="space-between"
                  sx={{ minWidth: 0 }}
                >
                  <Typography sx={{ fontSize: 12, color: leavePalette.subText }}>
                    {head.name}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: 12.5,
                      color: leavePalette.text,
                      fontWeight: 600,
                      minWidth: 0,
                      maxWidth: "58%",
                      textAlign: "right",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {renderAttendanceCell(row, head, meta)}
                  </Box>
                </Stack>
              ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export const AttendanceHistoryTable = ({
  headers = [],
  rows,
  meta,
  onRowClick,
}) => {
  const totalCols = headers.length || 1;
  return (
    <Box>
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        {rows.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              color: leavePalette.subText,
              border: `1px solid ${leavePalette.border}`,
              borderRadius: 2,
              backgroundColor: "#fff",
            }}
          >
            No {meta?.label?.toLowerCase() || "request"} records found.
          </Box>
        ) : (
          <Stack spacing={1.25}>
            {rows.map((row, idx) => (
              <AttendanceHistoryMobileCard
                key={row.id ?? idx}
                row={row}
                headers={headers}
                meta={meta}
                onClick={() => onRowClick(row)}
              />
            ))}
          </Stack>
        )}
      </Box>
      <TableContainer sx={{ overflowX: "auto", display: { xs: "none", sm: "block" } }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              {headers.map((head) => (
                <TableCell key={head.key} sx={headCellSx}>
                  {head.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableEmptyState
                colSpan={totalCols}
                dataLength={rows}
                title={`No ${meta?.label || "Request"} Records Found`}
                subtitle="There's nothing to show here yet."
              />
            ) : (
              rows.map((row, idx) => (
                <TableRow
                  key={row.id ?? idx}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => onRowClick(row)}
                >
                  {headers.map((head) => (
                    <TableCell key={head.key} sx={bodyCellSx}>
                      {renderAttendanceCell(row, head, meta)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
